import type {
  CampaignBannerMerchandising,
  CategoryBrandingMerchandising,
  CollectionMerchandising,
  CollectionUUID,
  CompactCampaignBannerMerchandising,
  Recipe,
  RecipeUUID,
} from '@library/menu-service'

import { useCollection } from 'hooks/menu-collections'
import { useRecipes } from 'hooks/menu-recipes/useRecipes'
import { useShouldRemoveMerchandisingBanner } from 'routes/Menu/MenuRecipesPage/CollectionHeader/useShouldRemoveMerchandisingBanner'

import {
  toCampaignBannerContentBlock,
  toCategoryBrandingContentBlock,
  toCompactCampaignBannerContentBlock,
} from './adapters'
import type { MerchandisingHeaderContentBlock } from './contentBlockTypes'

const isSupportedMerchandisingHeader = (
  merchandising: CollectionMerchandising,
): merchandising is
  | CategoryBrandingMerchandising
  | CampaignBannerMerchandising
  | CompactCampaignBannerMerchandising =>
  merchandising.type === 'category-branding' ||
  merchandising.type === 'campaign-banner' ||
  merchandising.type === 'compact-campaign-banner'

const getMerchandisingHeaderRecipeIds = (
  merchandisingHeader:
    | CategoryBrandingMerchandising
    | CampaignBannerMerchandising
    | CompactCampaignBannerMerchandising
    | undefined,
): RecipeUUID[] => {
  if (!merchandisingHeader) {
    return []
  }

  if (merchandisingHeader.type === 'campaign-banner') {
    return merchandisingHeader.items
      .filter(
        (item): item is Extract<CampaignBannerMerchandising['items'][number], { type: 'recipe' }> =>
          item.type === 'recipe',
      )
      .map((item) => item.id as RecipeUUID)
  }

  if (merchandisingHeader.type === 'compact-campaign-banner') {
    return merchandisingHeader.items.map((item) => item.id as RecipeUUID)
  }

  return []
}

export const useMerchandisingHeader = (
  collectionId: CollectionUUID | null,
): MerchandisingHeaderContentBlock | null => {
  const removeMerchandisingBanner = useShouldRemoveMerchandisingBanner()

  const collection = useCollection(collectionId)

  const merchandisingHeader = collection?.merchandising?.find(isSupportedMerchandisingHeader)
  const recipes = useRecipes(getMerchandisingHeaderRecipeIds(merchandisingHeader))

  if (!merchandisingHeader || removeMerchandisingBanner) {
    return null
  }

  const recipeById = Object.fromEntries(
    recipes.flatMap((recipe) => (recipe ? [[recipe.id, recipe]] : [])),
  ) as Record<string, Recipe>

  const getRecipeById = (id: string) => recipeById[id] ?? null

  switch (merchandisingHeader.type) {
    case 'category-branding':
      return toCategoryBrandingContentBlock(merchandisingHeader)
    case 'campaign-banner':
      return toCampaignBannerContentBlock(merchandisingHeader, getRecipeById)
    case 'compact-campaign-banner':
      return toCompactCampaignBannerContentBlock(merchandisingHeader, getRecipeById)
    default:
      return null
  }
}
